import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';

import { DocumentOntologyCatalog } from '@/services/database/documentIntelligence';
import {
    AuthoritativeConceptMapping,
    AuthoritativeConceptScheme,
    RESEARCH_ONTOLOGY_VERSION,
    ResearchDomainDefinition,
    ResearchDomainId,
} from '@/shared/document-intelligence/researchOntology';

import { ExtractedDocumentPage, extractPdfTextPages } from '../documentExtraction';
import { DOCUMENT_WORD_PATTERN, matchDocumentConcepts } from './documentConceptMatcher';

export interface DocumentConceptReference {
    context: string;
    height: number;
    id: string;
    page: number;
    textEnd: number;
    textStart: number;
    text: string;
    width: number;
    x: number;
    y: number;
    rectangles?: DocumentConceptReferenceRectangle[];
}

export interface DocumentConceptReferenceRectangle {
    height: number;
    width: number;
    x: number;
    y: number;
}

export interface DocumentConcept {
    classification: 'canonical' | 'document-specific';
    description: string;
    id: string;
    label: string;
    mappings: readonly AuthoritativeConceptMapping[];
    ontologyVersion: string;
    primaryDomainId?: ResearchDomainId;
    references: DocumentConceptReference[];
    secondaryDomainIds: readonly ResearchDomainId[];
}

export interface DocumentIntelligence {
    concepts: DocumentConcept[];
    domains: readonly ResearchDomainDefinition[];
    ontologyVersion: string;
    provenance: DocumentIntelligenceProvenance;
    schemes: readonly AuthoritativeConceptScheme[];
    wordCount: number;
}

export interface DocumentIntelligenceProvenance {
    extractionMethod: 'docx-rendered-text' | 'none' | 'pdf-text-layer' | 'plain-text';
    processingBoundary: 'client';
}

const getReference = (
    page: ExtractedDocumentPage,
    start: number,
    end: number,
    id: string,
    context: string,
    text: string,
): DocumentConceptReference | undefined => {
    const items = page.items.filter((candidate) => candidate.end > start && candidate.start < end);

    if (!items.length) {
        return undefined;
    }

    const rectangles = items.map((item) => {
        const itemLength = Math.max(item.end - item.start, 1);
        const relativeStart = (Math.max(start, item.start) - item.start) / itemLength;
        const relativeEnd = (Math.min(end, item.end) - item.start) / itemLength;
        const x =
            item.direction === 'rtl' ? item.x + item.width * (1 - relativeEnd) : item.x + item.width * relativeStart;

        return {
            height: item.height,
            width: item.width * Math.max(relativeEnd - relativeStart, 0),
            x,
            y: item.y,
        };
    });
    const x = Math.min(...rectangles.map((rectangle) => rectangle.x));
    const y = Math.min(...rectangles.map((rectangle) => rectangle.y));
    const right = Math.max(...rectangles.map((rectangle) => rectangle.x + rectangle.width));
    const bottom = Math.max(...rectangles.map((rectangle) => rectangle.y + rectangle.height));

    return {
        context,
        height: Math.min(bottom - y, 1 - y),
        id,
        page: page.page,
        textEnd: end,
        textStart: start,
        text,
        width: Math.min(right - x, 1 - x),
        x,
        y,
        rectangles,
    };
};

export const buildDocumentIntelligence = (
    pages: ExtractedDocumentPage[],
    ontology: DocumentOntologyCatalog,
): DocumentIntelligence => {
    const wordCount = pages.reduce((total, page) => total + [...page.text.matchAll(DOCUMENT_WORD_PATTERN)].length, 0);
    const concepts = matchDocumentConcepts(pages, ontology).flatMap<DocumentConcept>((concept) => {
        const references = concept.references.flatMap<DocumentConceptReference>((match) => {
            const page = pages.find((candidate) => candidate.page === match.page);
            const reference = page
                ? getReference(page, match.start, match.end, match.id, match.context, match.text)
                : undefined;

            return reference ? [reference] : [];
        });

        return references.length ? [{ ...concept, references }] : [];
    });

    return {
        concepts,
        domains: ontology.domains,
        ontologyVersion: ontology.version,
        provenance: { extractionMethod: 'pdf-text-layer', processingBoundary: 'client' },
        schemes: ontology.schemes,
        wordCount,
    };
};

/**
 * Applies the canonical ontology matcher to text-native resources such as
 * transcripts while retaining stable source offsets for bidirectional UI
 * navigation. Geometry is intentionally empty because the source is not a
 * paginated visual document.
 */
export const buildTextDocumentIntelligence = (
    text: string,
    ontology: DocumentOntologyCatalog,
): DocumentIntelligence => {
    const pages = [{ page: 1, text }];
    const concepts: DocumentConcept[] = matchDocumentConcepts(pages, ontology).map((concept) => ({
        classification: concept.classification,
        description: concept.description,
        id: concept.id,
        label: concept.label,
        mappings: concept.mappings,
        ontologyVersion: concept.ontologyVersion,
        primaryDomainId: concept.primaryDomainId,
        references: concept.references.map((reference) => ({
            context: reference.context,
            height: 0,
            id: reference.id,
            page: reference.page,
            text: reference.text,
            textEnd: reference.end,
            textStart: reference.start,
            width: 0,
            x: 0,
            y: 0,
        })),
        secondaryDomainIds: concept.secondaryDomainIds,
    }));

    return {
        concepts,
        domains: ontology.domains,
        ontologyVersion: ontology.version,
        provenance: { extractionMethod: 'plain-text', processingBoundary: 'client' },
        schemes: ontology.schemes,
        wordCount: [...text.matchAll(DOCUMENT_WORD_PATTERN)].length,
    };
};

export const extractDocumentIntelligence = async (
    pdf: PDFDocumentProxy,
    ontology: DocumentOntologyCatalog,
    signal?: AbortSignal,
): Promise<DocumentIntelligence> => buildDocumentIntelligence(await extractPdfTextPages(pdf, signal), ontology);

export const EMPTY_DOCUMENT_INTELLIGENCE: DocumentIntelligence = {
    concepts: [],
    domains: [],
    ontologyVersion: RESEARCH_ONTOLOGY_VERSION,
    provenance: { extractionMethod: 'none', processingBoundary: 'client' },
    schemes: [],
    wordCount: 0,
};
